import { ZH_TEMPLATES } from '../../constants/templates/zh';
import { EN_TEMPLATES } from '../../constants/templates/en';

export class LabTextGenerator {
    constructor(language = 'zh') {
        this.setLanguage(language);
    }

    setLanguage(language) {
        this.language = language;
        this.templates = language === 'zh' ? ZH_TEMPLATES : EN_TEMPLATES;
        this.mappings = this.templates.mappings;
    }

    generateBrightnessDescription(partitionName, analysis) {
        if (partitionName !== "global") {
            return this.templates.common.newline;
        }

        const L = analysis.L.statistics;
        const { mean: LMean, median: LMedian, min: LMin, max: LMax } = L;
        const brightnessLevel = analysis.L.classification.brightnessLevel;
        const rangeLevel = analysis.L.classification.dynamicRange;
        const skewnessClass = analysis.L.classification.skewnessClass;
        const kurtosisClass = analysis.L.classification.kurtosisClass;

        return this.templates.brightness.description.template
            .replace("${bullet}", this.templates.common.bullet)
            .replace("${brightnessLevel}", this.mappings.brightness[brightnessLevel])
            .replace("${rangeLevel}", this.mappings.range[rangeLevel])
            .replace("${skewnessClass}", this.mappings.skewness[skewnessClass])
            .replace("${and}", this.templates.common.conjunction.and)
            .replace("${kurtosisClass}", this.mappings.kurtosis[kurtosisClass])
            .replace("${bracketLeft}", this.templates.common.brackets.left)
            .replace("${bracketRight}", this.templates.common.brackets.right)
            .replace("${channelL}", this.templates.statistics.L)
            .replace("${mean}", this.templates.statistics.mean)
            .replace("${meanValue}", LMean.toFixed(2))
            .replace("${median}", this.templates.statistics.median)
            .replace("${medianValue}", LMedian)
            .replace("${range}", this.templates.statistics.range)
            .replace("${rangeValue}", `${LMin}-${LMax}`)
            .replace("${period}", this.templates.common.period)
            .replace("${comma}", this.templates.common.comma);
    }
    
    generateToneDescription(structuredConclusions, _partitionName, analysis) {
        // const prefix = partitionName === "global" ? 
        //     this.templates.prefixes.global : 
        //     `${partitionName}${this.templates.prefixes.area}`;

        if (!structuredConclusions.mainTone.tone) {
            return `${this.templates.incomplete.mainTone}${this.templates.common.newline}`;
        }

        const rules = [
            ...Object.values(structuredConclusions.mainTone.rules.mainTone || []),
            ...Object.values(structuredConclusions.mainTone.rules.intensity || []),
            ...Object.values(structuredConclusions.mainTone.rules.deviation || []),
            ...Object.values(structuredConclusions.mainTone.rules.stability || []),
            ...Object.values(structuredConclusions.mainTone.rules.range || [])
        ].filter(Boolean);

        let description = `${this.templates.common.bullet}`;
        
        rules.forEach((rule, index) => {
            const template = 
                this.templates.tones.rules.mainTone[rule] ||
                this.templates.tones.rules.toneIntensity[rule] ||
                this.templates.tones.rules.toneDeviation[rule] ||
                this.templates.tones.rules.toneStability[rule] ||
                this.templates.tones.rules.toneRange[rule];

            if (template) {
                description += `${template}${index < rules.length - 1 ? this.templates.common.comma : ''}`;
            }
        });

        const a = analysis.a.statistics;
        description += `${this.templates.common.brackets.left}${this.templates.statistics.a}${this.templates.common.comma}${this.templates.statistics.mode} ${a.mode.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.median} ${a.median.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.mean} ${a.mean.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.stdDev} ${a.stdDev.toFixed(1)}${this.templates.common.brackets.right}${this.templates.common.period}`;

        return description;
    }

    generateColorTemperatureDescription(structuredConclusions, partitionName, analysis) {
        // const prefix = partitionName === "global" ? 
        //     this.templates.prefixes.global : 
        //     `${partitionName}${this.templates.prefixes.area}`;

        if (!structuredConclusions.colorTemperature.tone) {
            return `${this.templates.incomplete.colorTemperature}${this.templates.common.newline}`;
        }

        const rules = [
            ...Object.values(structuredConclusions.colorTemperature.rules.tone || []),
            ...Object.values(structuredConclusions.colorTemperature.rules.intensity || []),
            ...Object.values(structuredConclusions.colorTemperature.rules.deviation || []),
            ...Object.values(structuredConclusions.colorTemperature.rules.stability || [])
        ].filter(Boolean);

        let description = `${this.templates.common.bullet}`;
        
        rules.forEach((rule, index) => {
            const template =
                this.templates.temperature.rules.tone[rule] ||
                this.templates.temperature.rules.intensity[rule] ||
                this.templates.temperature.rules.stability[rule] ||
                this.templates.temperature.rules.deviation[rule];

            if (template) {
                description += `${template}${index < rules.length - 1 ? this.templates.common.comma : ''}`;
            }
        });

        const b = analysis.b.statistics;
        description += `${this.templates.common.brackets.left}${this.templates.statistics.b}${this.templates.common.comma}${this.templates.statistics.mode} ${b.mode.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.median} ${b.median.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.mean} ${b.mean.toFixed(0)}${this.templates.common.comma}${this.templates.statistics.stdDev} ${b.stdDev.toFixed(1)}${this.templates.common.brackets.right}${this.templates.common.period}`;

        return description;
    }

    generateLocalColorDescription(structuredConclusions, _partitionName) {
        // const prefix = partitionName === "global" ? 
        //     this.templates.prefixes.global : 
        //     `${partitionName}${this.templates.prefixes.area}`;
        
        const localColor = structuredConclusions.localColor;
        let template;

        // 格式化颜色描述
        const formatColorInfo = (colorInfo) => {
            return colorInfo.map(info => 
                this.templates.localColor.description.colorFormat
                    .replace("${color}", this.templates.mappings.colors[info.color])
                    .replace("${channel}", this.templates.mappings.channels[info.channel])
                    .replace("${value}", info.value)
            ).join(this.templates.common.conjunction.and);
        };

        if (localColor.balance.balanceType === "both") {
            template = this.templates.localColor.description.both
                .replace("${highlights}", formatColorInfo(localColor.highlights))
                .replace("${lowlights}", formatColorInfo(localColor.lowlights));
        } else if (localColor.balance.balanceType === "highlights") {
            template = this.templates.localColor.description.highlights
                .replace("${highlights}", formatColorInfo(localColor.highlights));
        } else if (localColor.balance.balanceType === "lowlights") {
            template = this.templates.localColor.description.lowlights
                .replace("${lowlights}", formatColorInfo(localColor.lowlights));
        } else {
            template = this.templates.localColor.description.balanced;
        }

        return `${this.templates.common.bullet}${template}${this.templates.common.period}`;
    }

    generateTextDescriptions(structuredConclusions, partitionName, analysis) {
        const brightnessDescription = this.generateBrightnessDescription(partitionName, analysis);
        const mainToneDescription = this.generateToneDescription(structuredConclusions, partitionName, analysis);
        const colorTemperatureDescription = this.generateColorTemperatureDescription(structuredConclusions, partitionName, analysis);
        const localColorDescription = this.generateLocalColorDescription(structuredConclusions, partitionName);

        let overallConclusion;
        if (partitionName === "global") {
            overallConclusion = this.templates.format.globalConclusion.template
                .replace("${brightness}", brightnessDescription)
                .replace("${mainTone}", mainToneDescription)
                .replace("${temperature}", colorTemperatureDescription)
                .replace("${localColor}", localColorDescription)
                .replace(/\${doubleNewline}/g, this.templates.format.doubleNewline)
                .replace(/\${singleNewline}/g, this.templates.format.singleNewline);
        } else {
            overallConclusion = this.templates.format.partitionConclusion.template
                .replace("${mainTone}", mainToneDescription)
                .replace("${temperature}", colorTemperatureDescription)
                .replace("${localColor}", localColorDescription)
                .replace(/\${doubleNewline}/g, this.templates.format.doubleNewline)
                .replace(/\${singleNewline}/g, this.templates.format.singleNewline);
        }

        return {
            brightness: brightnessDescription,
            mainTone: mainToneDescription,
            colorTemperature: colorTemperatureDescription,
            localColor: localColorDescription,
            overallConclusion,
        };
    }
} 